<template>
  <div class="user">
    <TourGuide
      ref="tourGuideRef"
      :steps="studentSteps"
      type="student"
    />
    <div v-if="isMobile" class="user-menu" @click="$emit('toggle-search')">
      <IconGoogle name="search" size="1.5rem" />
    </div>
    <NotificationBell />
    <!-- Button -->
    <a ref="button" class="user-menu" @click="state.showProfile = !state.showProfile">

      <AvatarPlay
        :src="foto_usuario"
        :name="userName"
        custom-size="2rem"
      />
      <IconGoogle size="1rem" name="keyboard_arrow_down" color="var(--bordercolor)" />
    </a>

    <!-- Popup -->
    <div v-if="state.showProfile" v-click-away="() => state.showProfile = false" class="user-popup">
      <ul class="user-submenu" :class="{ 'user-submenu-rounded': !showTermsLinks }">
        <li>
          <div class="user-info">
            <AvatarPlay
              :src="foto_usuario"
              :name="userName"
              custom-size="2rem"
            />

            <div class="user-info-detail">
              <span class="user-info-detail-name">{{ userName }}</span>
              <span class="user-info-detail-email" :class="{ 'has-tooltip': emailMember }" :title="emailMember">
                {{ currentMemberEmail }}
              </span>
            </div>
          </div>
        </li>
        <li @click="state.showProfile = false">
          <a v-if="isOriginalAdmin" @click="updateTypeView">
            <IconGoogle color="var(--maincolor)" size="1.3rem" name="desktop_windows" />
            {{ $t("menu.logout.view_as") }} {{ typeView }}
          </a>

          <router-link to="/minha-conta">
            <IconGoogle color="var(--maincolor)" size="1.3rem" name="account_circle" />
            {{ $t("menu.profile") }}
          </router-link>

          <router-link v-if="isAdmin" to="/configuracoes">
            <IconGoogle color="var(--maincolor)" size="1.3rem" name="settings" />
            {{ $t("menu.logout.settings") }}
          </router-link>

          <router-link to="/my-courses">
            <IconGoogle color="var(--maincolor)" size="1.3rem" name="school" />
            {{ $t("menu.courses") }}
          </router-link>

          <a v-if="hasOtherPlatforms" @click="showMyPlataformsModal">
            <IconGoogle color="var(--maincolor)" size="1.3rem" name="language" />
            {{ $t("menu.my_plataforms") }}
          </a>

          <a v-if="!isAdmin" @click="startTour">
            <IconGoogle color="var(--maincolor)" size="1.3rem" name="devices" />
            {{ $t('menu.tour') }}
          </a>

          <a v-if="!currentThemeFixed" @click="toggleTheme">
            <IconGoogle color="var(--maincolor)" size="1.3rem" :name="toggleThemeIcon" />
            {{ $t(toggleThemeLabel) }}
          </a>

          <a href="#" class="user-logout" @click.prevent="logout">
            <IconGoogle color="var(--maincolor)" size="1.3rem" name="logout" />
            {{ $t("menu.logout.exit") }}
          </a>
        </li>
      </ul>
      <ul v-if="showTermsLinks" class="user-terms">
        <span v-if="showPrivacyPolicy" class="user-term-span user-terms--item" @click="goToLink(privacyPolicyLink)">
          {{ $t("menu.policy") }}
        </span>
        <svg
          v-if="showPrivacyPolicy && showUserTerms" xmlns="http://www.w3.org/2000/svg" width="3" height="4"
          viewBox="0 0 3 4" fill="none">
          <circle cx="1.5" cy="2" r="1.5" fill="#404043" />
        </svg>
        <span v-if="showUserTerms" class="user-term-span user-terms--item" @click="goToLink(userTermsLink)">
          {{ $t("terms_modal.title") }}
        </span>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
defineEmits(['toggle-search'])

import NotificationBell from "./NotificationBell.vue";
import TourGuide from "./TourGuide.vue";
import { IconGoogle, AvatarPlay } from "@/components/atomic";
import { reactive, computed, ref, onMounted } from "vue";
import { useStateStore } from "@/store/state.store";
import { useConfigStore } from "@/store/config.store";
import { useUserStore } from "@/store/user.store";
import { useAuthStore } from "@/store/auth.store";
import { useHomeLegacyStore } from "@/store/home-legacy.store";
;
import { modal } from "@/events";
import { useUIStore } from "@/store/useUIStore";
import { play } from "@voomp/play-back";
import { useStudentSteps } from "@/utils/tour-steps";

const stateStore = useStateStore();
const configStore = useConfigStore();
const userStore = useUserStore();
const authStore = useAuthStore();
const ui = useUIStore();

const tourGuideRef = ref<InstanceType<typeof TourGuide> | null>(null);
const studentSteps = useStudentSteps(tourGuideRef);

defineProps<{
  isMobile?: boolean
}>()

const state = reactive({
  showProfile: false,
  isMyPlataformsVisible: false,
})

const allSites = ref<Array<Record<string, unknown>>>([])

const hasOtherPlatforms = computed(() => {
  const currentSiteId = stateStore.site?.id;
  return allSites.value.some(site => site.id !== currentSiteId);
})

onMounted(async () => {
  if (!authStore.getMemberId() || !authStore.getSiteId()) return;
  try {
    allSites.value = (await play.site.list_by_current_member({})) as Array<Record<string, unknown>>;
  } catch {
    // not authenticated or endpoint unavailable
  }
})

const getSettings = computed(() => configStore.getSettings)
const getTheme = computed(() => ui.getTheme())
const isOriginalAdmin = computed(() => authStore.isOriginalAdmin())
const _metasSite = computed(() => userStore.getMetasSite)

const isAdmin = computed(() => authStore.isAdmin())

const typeView = computed(() => {
  return authStore.getStudentMode() ? "Admin" : "Aluno";
})

const currentThemeFixed = computed(() => {
  return stateStore.site?.['theme_fixed'] || stateStore.settings?.['theme_fixed_painel'];
})

const emailMember = computed(() => {
  return stateStore.member.email || null;
})

const currentMemberEmail = computed(() => {
  const maxLength = 25;
  const email = emailMember.value;

  if (email && email.length >= maxLength) {
    return email.slice(0, maxLength) + "...";
  }

  return email;
})

const foto_usuario = computed(() => {
  return stateStore.member?.picture || null;
})

const userName = computed(() => {
  if (stateStore.member.name) {
    const name = stateStore.member.name.split(" ");
    return name.length > 1 ? `${name[0]} ${name[1]}` : name[0];
  }
  return "";
})

const toggleThemeIcon = computed(() => {
  return getTheme.value == "dark" ? "light_mode" : "dark_mode";
})

const toggleThemeLabel = computed(() => {
  return getTheme.value == "dark" ? "menu.theme.light" : "menu.theme.dark";
})

const showTermsLinks = computed(() => {
  return showPrivacyPolicy.value || showUserTerms.value;
})

const showPrivacyPolicy = computed(() => {
  return getSettings.value.link_to_privacy_policy;
})

const showUserTerms = computed(() => {
  return getSettings.value.link_to_user_terms;
})

const privacyPolicyLink = computed(() => {
  return getSettings.value.link_to_privacy_policy;
})

const userTermsLink = computed(() => {
  return getSettings.value.link_to_user_terms;
})

function showMyPlataformsModal() {
  modal.show("MyPlataformsSidebar", {})
}

function _onClose() {
  state.showProfile = false;
}

function goToLink(link) {
  window.open(link, "_blank");
  state.showProfile = false;
}

function toggleTheme() {
  ui.toogleTheme()
  state.showProfile = false;
}

function startTour() {
  state.showProfile = false;
  tourGuideRef.value?.startTourTopBar();
}

function updateTypeView() {
  authStore.toggleStudentMode();
  const homeLegacyStore = useHomeLegacyStore();
  const homeData = homeLegacyStore.getHomeData;
  homeLegacyStore.setHomeData({
    ...homeData,
    categories: (homeData.categories ?? []).map((cat: Record<string, unknown>) => ({ ...cat, courses: undefined })),
  });
}

function logout() {
  authStore.logout();
}

</script>

<style lang="scss" scoped>
.user {
  display: flex;
  flex-direction: row;
  position: relative;
  gap: 1rem;
  align-items: center;
}

.user-menu {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.user-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
}

.user-info-detail {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  color: var(--fontcolor);
}

.user-info-detail-name {
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  /* 1.05rem */
}

.user-info-detail-email {
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  /* 0.9rem */
}

.has-tooltip {
  cursor: pointer;
}

.user-popup {
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 0;
  top: 3rem;
  width: 17rem;
  // box-shadow: 2px 6px 10px rgba(0, 0, 0, 0.05);
  z-index: 55555 !important;
  animation: showRight 0.3s forwards;
}

/* Sub menu */
.user-submenu {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 0;
  padding: 1.25rem;
  align-items: flex-start;
  gap: 1.25rem;
  border-radius: 0.75rem 0.75rem 0rem 0rem;
  border: 1px solid var(--sidebar-border);
  background: var(--backgroundcolor);

  &-rounded {
    border-radius: 0.75rem 0.75rem 0.75rem 0.75rem;
  }
}

.user-submenu a {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 200px;
  padding: 10px;
  padding-left: 0;
  cursor: pointer;
  /* padding-left: 40px; */
  color: #333;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 15px;
  color: transparent;
  margin: 0 auto;
}

.user-submenu a img,
.user-submenu a svg {
  filter: invert(50%);
  transition: all 0.3s ease;
  margin: 0;
  margin-right: 35px;
}

.user-submenu a:hover img,
.user-submenu a:hover svg {
  :deep(svg ) {
    width: 1.25rem;
    height: 1.25rem;

    path {
      stroke: var(--maincolor);
    }
  }
}

a:hover {
  text-decoration: none !important;
}

.user-submenu li a {
  font-weight: 600;
  color: var(--fontcolor);
  font-size: 14px;
  padding: 10px;
  display: flex;
  gap: var(--spacing-16);

  /* background: silver; */
  :deep(svg ) {
    width: 1.25rem;
    height: 1.25rem;

    path {
      fill: var(--maincolor);
    }
  }
}

.user-submenu li a:hover {
  text-decoration: none;
  color: var(--maincolor);
}

.user-submenu li a.router-link-exact-active::before {
  /* display: block;
  position: absolute; */
  left: -15px;
}

.user-submenu li a+a {
  margin-top: 10px;
}

.user-submenu li a img,
.user-submenu li a svg {
  margin-right: 25px;
}

.user-terms {
  display: flex;
  padding: 1.25rem;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;
  align-self: stretch;
  border-radius: 0rem 0rem 0.75rem 0.75rem;
  border-right: 1px solid var(--sidebar-border);
  border-bottom: 1px solid var(--sidebar-border);
  border-left: 1px solid var(--sidebar-border);
  background: var(--backgroundcolor);

  &--item {
    cursor: pointer;
  }
}

.user-term-span {
  color: var(--fontcolor2, #404043);
  /* Body/Regular/10px */
  font-size: 0.625rem;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  /* 0.75rem */
}

.user-logout:hover {
  color: #ff0c37 !important;
}

li::marker {
  color: transparent !important;
}
</style>
